"use client";

import type {
    LomdaScene,
    SceneInteraction,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";
import {
    isMediaElement,
    type SceneElement,
} from "@/features/lomdaEditorV2/lib/elements/types";
import type { SelectedMediaItemType } from "@/features/lomdaEditorV2/lib/types";
import { useEditorStore, useEditorStoreShallow } from "./context";
import type {
    AssetsSlice,
    DocumentSlice,
    EditorState,
    GapId,
    UiSlice,
} from "./types";

const EMPTY_ELEMENTS: SceneElement[] = [];
const EMPTY_INTERACTIONS: SceneInteraction[] = [];

// ── State selectors ────────────────────────────────────────────────────────
export const useScenesList = () => useEditorStore((s) => s.scenes);
export const useSceneCount = () => useEditorStore((s) => s.scenes.length);
export const useSelectedSceneId = () =>
    useEditorStore((s) => s.selectedSceneId);

export const useScene = (sceneId: string | null): LomdaScene | null =>
    useEditorStore((s) =>
        sceneId ? (s.scenes.find((x) => x.id === sceneId) ?? null) : null
    );

export const useSelectedScene = (): LomdaScene | null =>
    useEditorStore(
        (s) => s.scenes.find((x) => x.id === s.selectedSceneId) ?? null
    );

export const useSceneElements = (sceneId: string | null): SceneElement[] =>
    useEditorStore((s) =>
        sceneId
            ? (s.scenes.find((x) => x.id === sceneId)?.elements ??
              EMPTY_ELEMENTS)
            : EMPTY_ELEMENTS
    );

export const useElement = (
    sceneId: string | null,
    elementId: string | null
): SceneElement | null =>
    useEditorStore((s) => {
        if (!sceneId || !elementId) return null;
        const scene = s.scenes.find((x) => x.id === sceneId);
        return scene?.elements?.find((e) => e.id === elementId) ?? null;
    });

export const useSelection = () => useEditorStore((s) => s.selection);

export const useSelectedElementId = (): string | null =>
    useEditorStore((s) =>
        s.selection.kind === "element" || s.selection.kind === "block-image"
            ? s.selection.elementId
            : null
    );

/** Legacy media-selection shape: is the scene container or a media element selected? */
export const useSelectedMediaItemType = (): SelectedMediaItemType =>
    useEditorStore((s) => {
        if (s.selection.kind === "scene") return "scene-container";
        if (s.selection.kind === "element") {
            const elementId = s.selection.elementId;
            const scene = s.scenes.find((x) => x.id === s.selectedSceneId);
            const el = scene?.elements?.find((e) => e.id === elementId);
            return el && isMediaElement(el) ? "scene" : null;
        }
        return null;
    });

export const useInteractionsForScene = (gapId: GapId): SceneInteraction[] =>
    useEditorStore(
        (s) =>
            s.scenes.find((x) => x.id === gapId)?.interactionsAfter ??
            EMPTY_INTERACTIONS
    );

export const useActiveTab = () => useEditorStore((s) => s.activeTab);
export const useShowFloatingNav = () =>
    useEditorStore((s) => s.showFloatingNav);
export const useSaveStatus = () => useEditorStore((s) => s.save);
export const useLiveTransform = () => useEditorStore((s) => s.liveTransform);
/** The live transform only if `elementId` is the one being dragged, else null —
 *  so a gesture re-renders just the active element, not every canvas element. */
export const useLiveTransformFor = (elementId: string) =>
    useEditorStore((s) =>
        s.liveTransform?.elementId === elementId
            ? s.liveTransform.transform
            : null
    );
export const useLiveKind = () => useEditorStore((s) => s.liveKind);
export const useLiveGuides = () => useEditorStore((s) => s.liveGuides);
/** Whether any move/resize/rotate gesture is currently in progress. */
export const useIsTransforming = () =>
    useEditorStore((s) => s.liveTransform !== null);
export const useActiveTextEditorId = () =>
    useEditorStore((s) => s.activeTextEditorId);
export const usePendingCaret = () => useEditorStore((s) => s.pendingCaret);
export const useMetadata = () => useEditorStore((s) => s.metadata);
export const useLomdaName = () => useEditorStore((s) => s.lomdaName);
export const useAssets = () => useEditorStore((s) => s.assets);
export const useChapters = () => useEditorStore((s) => s.chapters);
export const useInteractionModal = () =>
    useEditorStore((s) => s.interactionModal);
export const useRenderModalOpen = () =>
    useEditorStore((s) => s.renderModalOpen);
export const useActiveRenderRunId = () =>
    useEditorStore((s) => s.activeRenderRunId);
export const useIsGeneratingSceneAudio = (sceneId: string) =>
    useEditorStore((s) => s.isGeneratingSceneAudio[sceneId] ?? false);

export const useIdentity = () =>
    useEditorStoreShallow((s) => ({
        userId: s.userId,
        lomdaId: s.lomdaId,
        mode: s.mode,
        readOnly: s.readOnly,
    }));
export const useEditorMode = () => useEditorStore((s) => s.mode);

// ── Action groups (stable refs via shallow equality) ───────────────────────
const documentActionKeys: (keyof DocumentSlice)[] = [
    "addScene",
    "updateScene",
    "deleteScene",
    "duplicateScene",
    "reorderScenes",
    "addElement",
    "updateElement",
    "updateElementTransform",
    "updateElementMedia",
    "setTextElementHtml",
    "setTextVerticalAlign",
    "setAvatarConfig",
    "setAvatarFullScreen",
    "applyAvatarToAllScenes",
    "setSceneAvatar",
    "removeElement",
    "reorderElementZ",
    "applyLayout",
    "renameChapter",
    "moveSceneToPreviousChapter",
    "moveSceneToNextChapter",
    "isolateSceneAsChapter",
    "splitChapterAt",
    "reorderChapters",
    "moveSceneToChapter",
    "addInteractionToGap",
    "updateInteractionInGap",
    "deleteInteractionFromGap",
    "moveInteractionToGap",
    "updateMetadata",
    "setLomdaName",
];

const uiActionKeys: (keyof UiSlice)[] = [
    "setSelectedSceneId",
    "selectElement",
    "selectScene",
    "clearSelection",
    "setSelectedBlockImage",
    "setActiveTextEditorId",
    "enterTextEdit",
    "clearPendingCaret",
    "beginLiveTransform",
    "setLiveFrame",
    "clearLiveGuides",
    "endLiveTransform",
    "setActiveTab",
    "onChangeActiveTab",
    "setShowFloatingNav",
    "setSaveStatus",
    "setGeneratingSceneAudio",
    "openInteractionCreate",
    "openInteractionEdit",
    "closeInteractionModal",
    "setRenderModalOpen",
    "setActiveRenderRunId",
];

const assetsActionKeys: (keyof AssetsSlice)[] = [
    "addAsset",
    "addAssetFromUpload",
    "deleteAsset",
    "replaceAssets",
];

function pick<K extends keyof EditorState>(
    s: EditorState,
    keys: K[]
): Pick<EditorState, K> {
    const out = {} as Pick<EditorState, K>;
    for (const k of keys) out[k] = s[k];
    return out;
}

export const useDocumentActions = () =>
    useEditorStoreShallow((s) => pick(s, documentActionKeys));
export const useUiActions = () =>
    useEditorStoreShallow((s) => pick(s, uiActionKeys));
export const useAssetsActions = () =>
    useEditorStoreShallow((s) => pick(s, assetsActionKeys));
